import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {Alert, Badge, Button, Form, Spinner, ButtonGroup, Dropdown, DropdownButton} from 'react-bootstrap'
import axios from 'axios'
import {useDisclourse} from '../../hooks/useDisclourse'
import {DrawerAddLead} from './components/DrawerAddLead'
import {IEdgesStagePipe, IFilterStage, IInfoLead} from './leadTypes'
import {DrawerEditLead} from './components/DrawerEditLead'
import {useApolloClient, useQuery} from '@apollo/client'
import {GET_ALL_PIPELINES} from '../../gql/queries/pipelines'
import {IResPipelines, IStagePipelineSet} from '../../types/pipelines'
import {LoadingLeadsPage} from './components/LoadingLeadsPage'
import {toast} from 'react-toastify'
import {ContentDragDropLeads} from './components/ContentDragDropLeads'
import {useGetUser} from '../../store/userStore'
import {hasPermission, parseId} from '../../helpers'
import {ALL_PERMISSIONS, STORAGE_NAMES} from '../../helpers/consts'
import {ContentListLeads} from './components/ContentListLeads'
import {DateInput} from '../../components/DateInput'
import {IResAllListUsers} from '../../types/users'
import {GET_ALL_USERS_LIST} from '../../gql/queries/usersQuery'
import {ModalReasingLeads} from './components/ModalReasingLeads'
import {ModalAssignAgentsToPipeline} from './components/ModalAssignAgentsToPipeline'
import {URL as BACKEND_URL, fetchNewToken} from '../../gql/client'
import {useTelephony} from '../../hooks/useTelephony'
import {TelephonyDialQueueResponse, TelephonyDialQueueSummary} from '../../types/telephony'
import {useSearchParams} from 'react-router-dom'

export const STATUS_LEADS = {
  Declined: 'Declined',
  Sold: 'Sold',
}

export const SEMAFORO_LIMITS = {
  red: 5,
  green: 3,
}

export const getClassTraffic = (dateLead: Date) => {
  const ahora = new Date()
  const diferenciaMs = ahora.getTime() - dateLead.getTime()
  const dias = Math.floor(diferenciaMs / (1000 * 60 * 60 * 24))
  if (dias >= SEMAFORO_LIMITS.red) {
    return {
      class: 'traffic-red',
      title: 'This element has ' + SEMAFORO_LIMITS.red + ' days old',
      diff: dias,
    }
  }
  if (dias >= SEMAFORO_LIMITS.green && dias < SEMAFORO_LIMITS.red) {
    return {
      class: 'traffic-orange',
      title: 'This element has ' + SEMAFORO_LIMITS.green + ' days old',
      diff: dias,
    }
  }
  return {
    class: 'traffic-green',
    title: 'This element has ' + SEMAFORO_LIMITS.green + ' days old',
    diff: dias,
  }
}

// Helper function to get default date range (1 month ago to tomorrow)
const getDefaultDateRange = () => {
  const today = new Date()

  // Date FROM: First day of 1 month ago at 00:00:00
  const dateFrom = new Date(today.getFullYear(), today.getMonth() - 1, 1)
  dateFrom.setHours(0, 0, 0, 0)

  // Date TO: Tomorrow at 23:59:59.999
  const dateTo = new Date(today)
  dateTo.setDate(dateTo.getDate() + 1)
  dateTo.setHours(23, 59, 59, 999)

  return { dateFrom, dateTo }
}

// Helper function to format date to YYYY-MM-DD
const formatDate = (date: Date | null) => {
  if (!date) return ''
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

// Helper functions for localStorage
const LEADS_STORAGE_KEY = 'leads-last-pipeline'
const LEADS_STORAGE_USER_KEY = 'leads-last-user-id'
const LEADS_VIEW_STORAGE_KEY = 'leads-view-mode'

const getPipelineStorageKey = (userId: string | null) =>
  userId ? `${LEADS_STORAGE_KEY}:${userId}` : LEADS_STORAGE_KEY

const getStoredPipelineId = (userId: string | null): string | null => {
  try {
    return window.localStorage.getItem(getPipelineStorageKey(userId))
  } catch {
    return null
  }
}

const setStoredPipelineId = (userId: string | null, pipelineId: string) => {
  try {
    window.localStorage.setItem(getPipelineStorageKey(userId), pipelineId)
  } catch {
    // Fail silently in incognito mode
  }
}

const getStoredUserId = (): string | null => {
  try {
    return window.localStorage.getItem(LEADS_STORAGE_USER_KEY)
  } catch {
    return null
  }
}

const setStoredUserId = (userId: string) => {
  try {
    window.localStorage.setItem(LEADS_STORAGE_USER_KEY, userId)
  } catch {
    // Fail silently in incognito mode
  }
}

const getStoredLeadsView = (): 'card' | 'list' => {
  try {
    const storedView = window.localStorage.getItem(LEADS_VIEW_STORAGE_KEY)
    return storedView === 'list' ? 'list' : 'card'
  } catch {
    return 'card'
  }
}

const setStoredLeadsView = (view: 'card' | 'list') => {
  try {
    window.localStorage.setItem(LEADS_VIEW_STORAGE_KEY, view)
  } catch {
    // Fail silently in incognito mode
  }
}

const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem(STORAGE_NAMES.TOKEN) || ''}`,
})

const authGet = async <T,>(url: string) => {
  try {
    return await axios.get<T>(url, {headers: getAuthHeaders()})
  } catch (error: any) {
    if (error?.response?.status === 401) {
      const newToken = await fetchNewToken()
      if (newToken) {
        return await axios.get<T>(url, {
          headers: {Authorization: `Bearer ${newToken}`},
        })
      }
    }
    throw error
  }
}

const authPost = async <T,>(url: string, data: Record<string, unknown>) => {
  try {
    return await axios.post<T>(url, data, {headers: getAuthHeaders()})
  } catch (error: any) {
    if (error?.response?.status === 401) {
      const newToken = await fetchNewToken()
      if (newToken) {
        return await axios.post<T>(url, data, {
          headers: {Authorization: `Bearer ${newToken}`},
        })
      }
    }
    throw error
  }
}

export const LeadsPage = () => {
  const apolloClient = useApolloClient()
  const [searchParams, setSearchParams] = useSearchParams()
  const user = useGetUser((s) => s.user)
  const permissions = useGetUser((s) => s.user?.permissions)
  const telephony = useTelephony()
  const isRingIntegration = localStorage.getItem(STORAGE_NAMES.RING_CENTRAL) ? true : false
  const currentUserId = user?.user?.id ? String(user.user.id) : null
  const currentUserGroup = user?.user?.primaryGroup?.name || ''
  const canCreateSharedDialQueue = ['Administrador', 'SuperAdministrador', 'Supervisor'].includes(
    currentUserGroup
  )

  // Only fetch users list if user has export permission (Admins only)
  const canExportLeads = permissions ? hasPermission(permissions, ALL_PERMISSIONS.EXPORT_LEAD) : false
  const defaultsToMyLeads = ['Agente', 'Asesor'].includes(currentUserGroup)
  const canUsePolicyDialQueue = Boolean(currentUserId && telephony.config.vicidial_enabled)

  const {
    data: dataPipelines,
    loading: loadingPipelines,
    error: errorPipelines,
  } = useQuery<IResPipelines>(GET_ALL_PIPELINES, {
    fetchPolicy: 'cache-first', // Use cache for pipelines (rarely change)
  })

  const {data: dataMembers, error: errorMembers} = useQuery<IResAllListUsers>(GET_ALL_USERS_LIST, {
    fetchPolicy: 'cache-first', // Use cache for users list (rarely changes)
    skip: !canExportLeads, // Skip query if user doesn't have export permission
  })

  const [pipelineId, setPipelineId] = useState(() => getStoredPipelineId(currentUserId) || '')

  const [flag, setFlag] = useState(0)
  const [pageRefresh, setPageRefresh] = useState(0)

  const [flagView, setFlagView] = useState<'card' | 'list'>(() => getStoredLeadsView())
  const [typeAssign, setTypeAssign] = useState<'user' | 'group'>('user')

  const [isLoadingChangeState, setIsLoadingChangeState] = useState(false)
  const [isExport, setIsExport] = useState(false)

  const [infoLead, setInfoLead] = useState<null | IInfoLead>(null)
  const [infoReAssign, setInfoReAssign] = useState<null | {
    fromUserId: string
    fromUserName: string
    pipelineId: string
    pipelineName: string
    statePipeline: string
    stateNamePipeline: string
    searchName: string
    searchPhone: string
    dateFrom: Date | null
    dateTo: Date | null
    assignType: string
    leadsSelected?: {id: string; name: string; stage: string; user: string}[]
  }>(null)

  const [leadsSelected, setLeadSelected] = useState<
    {id: string; name: string; stage: string; user: string}[] | undefined
  >([])

  const [stageByPipeline, setStageByPipeline] = useState<IStagePipelineSet[] | null>(null)

  const [infoAssignPipeline, setInfoAssignPipeline] = useState<null | {
    pipelineName: string
    pipelineId: string
  }>(null)
  const {isOpen: isOpenEdit, onClose: onCloseEdit, onOpen: onOpenEdit} = useDisclourse()
  const {isOpen: isOpenAdd, onClose: onCloseAdd, onOpen: onOpenAdd} = useDisclourse()
  const {isOpen: isOpenReAssign, onClose: onCloseReAssign, onOpen: onOpenReAssign} = useDisclourse()
  const {
    isOpen: isOpenAssignPipeline,
    onClose: onCloseAssignPipeline,
    onOpen: onOpenAssignPipeline,
  } = useDisclourse()
  const [filterStage, setFilterStage] = useState<IFilterStage>('all')
  const [statePipeline, setStagePipelineId] = useState('')
  const [searchName, setSearchName] = useState('')
  const [searchPhone, setSearchPhone] = useState('')
  const [searchPhoneText, setSearchPhoneText] = useState('')
  const [searchNameText, setSearchNameText] = useState('')
  const [valueUser, setValueUser] = useState('')
  const [didInitMyLeads, setDidInitMyLeads] = useState(false)
  const [stagesDeclined, setStagesDeclined] = useState<IEdgesStagePipe | null>(null)
  const [dialQueue, setDialQueue] = useState<TelephonyDialQueueSummary | null>(null)
  const [loadingDialQueue, setLoadingDialQueue] = useState(false)
  const [mutatingDialQueue, setMutatingDialQueue] = useState(false)
  const lastDialQueueIdRef = useRef<number | null>(null)
  const openedLeadDeepLinkRef = useRef<string | null>(null)
  const leadDeepLinkPipelineId = searchParams.get('pipelineId')
  const leadDeepLinkLeadId = searchParams.get('leadId')

  // Initialize with default date range (3 months ago to tomorrow)
  const [dateFrom, setDateFrom] = useState<Date | null>(() => getDefaultDateRange().dateFrom)
  const [dateTo, setDateTo] = useState<Date | null>(() => getDefaultDateRange().dateTo)

  const stagesBySelectedPipeline = useMemo(() => {
    if (!dataPipelines || !pipelineId) return null
    const selectedPipeline = dataPipelines.allPipelines.edges.find(({node}) => node.id === pipelineId)
    return selectedPipeline?.node.stagePipelineSet?.edges ?? null
  }, [dataPipelines, pipelineId])

  const isMyLeadsActive = Boolean(currentUserId && valueUser === currentUserId)
  const effectiveQueueCampaignId = useMemo(
    () =>
      String(
        telephony.vicidialSession.selectedCampaignId ||
          telephony.vicidialSession.campaignId ||
          telephony.config.vicidial_campaign_id ||
          ''
      ).trim(),
    [
      telephony.config.vicidial_campaign_id,
      telephony.vicidialSession.campaignId,
      telephony.vicidialSession.selectedCampaignId,
    ]
  )
  const selectedLeadCount = leadsSelected?.length || 0
  const showStartDialQueueButton =
    flagView === 'list' &&
    canUsePolicyDialQueue &&
    (isMyLeadsActive || canCreateSharedDialQueue) &&
    selectedLeadCount > 0 &&
    !dialQueue

  const loadDialQueue = useCallback(
    async (silent = false) => {
      if (!canUsePolicyDialQueue) {
        setDialQueue(null)
        return null
      }

      if (!silent) {
        setLoadingDialQueue(true)
      }

      try {
        const response = await authGet<TelephonyDialQueueResponse>(
          `${BACKEND_URL}/api/telephony/dial-queue/current`
        )
        const nextQueue = response.data.queue || null
        setDialQueue(nextQueue)
        return nextQueue
      } catch (error: any) {
        const message = error?.response?.data?.error || error?.message || 'Unable to load the auto dial queue.'
        if (!silent) {
          toast.error(message)
        }
        return null
      } finally {
        if (!silent) {
          setLoadingDialQueue(false)
        }
      }
    },
    [canUsePolicyDialQueue]
  )

  const refreshList = useCallback(() => {
    setPageRefresh((p) => p + 1)
  }, [])

  useEffect(() => {
    if (!currentUserId) return
    const lastUserId = getStoredUserId()
    if (lastUserId && lastUserId !== currentUserId) {
      apolloClient.cache.evict({fieldName: 'allPipelines'})
      apolloClient.cache.gc()
      setPipelineId('')
    }
    setStoredUserId(currentUserId)
    const storedPipelineId = getStoredPipelineId(currentUserId)
    if (storedPipelineId) {
      setPipelineId(storedPipelineId)
    }
  }, [apolloClient, currentUserId])

  useEffect(() => {
    if (didInitMyLeads || !currentUserId) return
    if (defaultsToMyLeads) {
      setValueUser(currentUserId)
    }
    setDidInitMyLeads(true)
  }, [currentUserId, defaultsToMyLeads, didInitMyLeads])

  useEffect(() => {
    if (!pipelineId) return
    if (flagView === 'list') {
      setStagePipelineId('')
      refreshList()
    }
    setLeadsSelected([])
  }, [pipelineId, flagView])

  useEffect(() => {
    if (!dataPipelines) return
    if (dataPipelines.allPipelines.edges.length > 0) {
      if (leadDeepLinkPipelineId) {
        const pipelineFromUrl = dataPipelines.allPipelines.edges.find(
          ({node}) =>
            node.id === leadDeepLinkPipelineId ||
            String(parseId(node.id)) === String(leadDeepLinkPipelineId)
        )
        if (pipelineFromUrl) {
          setFlagView('card')
          setPipelineId(pipelineFromUrl.node.id)
          setStoredPipelineId(currentUserId, pipelineFromUrl.node.id)
          return
        }
      }

      const storedId = getStoredPipelineId(currentUserId)
      // Check if stored pipeline exists in available pipelines
      const storedPipelineExists = storedId &&
        dataPipelines.allPipelines.edges.some(edge => edge.node.id === storedId)

      if (storedPipelineExists) {
        setPipelineId(storedId)
      } else {
        // Fallback to first pipeline if stored one doesn't exist
        const firstPipelineId = dataPipelines.allPipelines.edges[0].node.id
        setPipelineId(firstPipelineId)
        setStoredPipelineId(currentUserId, firstPipelineId)
      }
    }
  }, [currentUserId, dataPipelines, leadDeepLinkPipelineId])

  useEffect(() => {
    if (!leadDeepLinkLeadId) return

    const deepLinkKey = `${leadDeepLinkPipelineId || ''}:${leadDeepLinkLeadId}`
    if (openedLeadDeepLinkRef.current === deepLinkKey) return

    if (leadDeepLinkPipelineId) {
      if (!dataPipelines) return
      const pipelineFromUrl = dataPipelines.allPipelines.edges.find(
        ({node}) =>
          node.id === leadDeepLinkPipelineId ||
          String(parseId(node.id)) === String(leadDeepLinkPipelineId)
      )
      if (pipelineFromUrl && pipelineId !== pipelineFromUrl.node.id) {
        return
      }
    }

    setFlagView('card')
    setInfoLead({id: leadDeepLinkLeadId, name: searchParams.get('leadName') || 'Lead'})
    onOpenEdit()
    openedLeadDeepLinkRef.current = deepLinkKey
  }, [
    dataPipelines,
    leadDeepLinkLeadId,
    leadDeepLinkPipelineId,
    onOpenEdit,
    pipelineId,
    searchParams,
  ])

  useEffect(() => {
    if (!stagesBySelectedPipeline) {
      setStagesDeclined(null)
      setStageByPipeline(null)
      return
    }
    const declinedStage = stagesBySelectedPipeline.find(({node}) => node.name === STATUS_LEADS.Declined)
    setStagesDeclined(declinedStage ?? null)
    setStageByPipeline(stagesBySelectedPipeline)
    setLeadsSelected([])
  }, [stagesBySelectedPipeline])

  useEffect(() => {
    if (errorPipelines) toast.error(`Error: ${errorPipelines.message}`)
  }, [errorPipelines])

  // Save selected pipeline to localStorage whenever it changes
  useEffect(() => {
    if (pipelineId) {
      setStoredPipelineId(currentUserId, pipelineId)
    }
  }, [currentUserId, pipelineId])

  useEffect(() => {
    setStoredLeadsView(flagView)
  }, [flagView])

  useEffect(() => {
    if (!canUsePolicyDialQueue) return

    loadDialQueue()
    const intervalId = window.setInterval(() => {
      loadDialQueue(true)
    }, 5000)

    return () => {
      window.clearInterval(intervalId)
    }
  }, [canUsePolicyDialQueue, loadDialQueue])

  useEffect(() => {
    const currentQueueId = dialQueue?.id ?? null
    if (lastDialQueueIdRef.current && !currentQueueId) {
      refreshList()
    }
    lastDialQueueIdRef.current = currentQueueId
  }, [dialQueue, refreshList])

  const updateListLeads = () => {
    setLeadsSelected([])
    console.log('leadsSelected', leadsSelected)
    setFlag((p) => p + 1)
  }

  const handleCloseEditLead = () => {
    onCloseEdit()
    if (!leadDeepLinkLeadId) return

    openedLeadDeepLinkRef.current = null
    const nextParams = new URLSearchParams(searchParams)
    nextParams.delete('leadId')
    nextParams.delete('leadName')
    setSearchParams(nextParams, {replace: true})
  }

  const setLeadsSelected = (leads: {id: string; name: string; stage: string; user: string}[]) => {
    setLeadSelected(leads)
  }

  const research = () => {
    updateListLeads()
  }

  const handleToggleMyLeads = () => {
    if (!currentUserId) return
    setLeadsSelected([])
    setValueUser((current) => (current === currentUserId ? '' : currentUserId))
  }

  const handleStartDialQueue = async () => {
    if (!canUsePolicyDialQueue) {
      toast.error('PolicyDial auto dial queue is not enabled for this account.')
      return
    }

    const leadIds = (leadsSelected || [])
      .map((lead) => Number(parseId(lead.id)))
      .filter((leadId) => Number.isFinite(leadId) && leadId > 0)

    if (!leadIds.length) {
      toast.error('Select at least one of your leads before starting the queue.')
      return
    }

    setMutatingDialQueue(true)
    try {
      const response = await authPost<TelephonyDialQueueResponse>(
        `${BACKEND_URL}/api/telephony/dial-queue/start`,
        {
          provider: 'VICIDIAL',
          campaign_id: effectiveQueueCampaignId || undefined,
          lead_ids: leadIds,
        }
      )

      const nextQueue = response.data.queue || null
      setDialQueue(nextQueue)
      setLeadsSelected([])
      refreshList()

      if (response.data.started) {
        toast.success('Auto dial queue started in PolicyDial.')
      } else if (nextQueue?.last_error) {
        toast.warning(nextQueue.last_error)
      } else {
        toast.info('Auto dial queue created and waiting for the next call.')
      }
    } catch (error: any) {
      const message = error?.response?.data?.error || error?.message || 'Unable to start the auto dial queue.'
      toast.error(message)
    } finally {
      setMutatingDialQueue(false)
    }
  }

  const handlePauseDialQueue = async () => {
    if (!dialQueue) return
    setMutatingDialQueue(true)
    try {
      const response = await authPost<TelephonyDialQueueResponse>(
        `${BACKEND_URL}/api/telephony/dial-queue/pause`,
        {queue_id: dialQueue.id}
      )
      setDialQueue(response.data.queue || null)
      toast.success('Auto dial queue paused.')
    } catch (error: any) {
      const message = error?.response?.data?.error || error?.message || 'Unable to pause the auto dial queue.'
      toast.error(message)
    } finally {
      setMutatingDialQueue(false)
    }
  }

  const handleResumeDialQueue = async () => {
    if (!dialQueue) return
    setMutatingDialQueue(true)
    try {
      const response = await authPost<TelephonyDialQueueResponse>(
        `${BACKEND_URL}/api/telephony/dial-queue/resume`,
        {
          queue_id: dialQueue.id,
          provider: 'VICIDIAL',
        }
      )
      const nextQueue = response.data.queue || null
      setDialQueue(nextQueue)
      if (response.data.started) {
        toast.success('Auto dial queue resumed.')
      } else {
        toast.info('Auto dial queue resumed. The current call remains in progress.')
      }
    } catch (error: any) {
      const message = error?.response?.data?.error || error?.message || 'Unable to resume the auto dial queue.'
      toast.error(message)
    } finally {
      setMutatingDialQueue(false)
    }
  }

  const handleStopDialQueue = async () => {
    if (!dialQueue) return
    setMutatingDialQueue(true)
    try {
      await authPost<TelephonyDialQueueResponse>(
        `${BACKEND_URL}/api/telephony/dial-queue/stop`,
        {queue_id: dialQueue.id}
      )
      setDialQueue(null)
      toast.success('Auto dial queue stopped.')
    } catch (error: any) {
      const message = error?.response?.data?.error || error?.message || 'Unable to stop the auto dial queue.'
      toast.error(message)
    } finally {
      setMutatingDialQueue(false)
    }
  }

  useEffect(() => {
    if (errorMembers) toast.error(`Error: ${errorMembers.message}`)
  }, [errorMembers])

  if (loadingPipelines || !dataPipelines || !permissions) {
    return <LoadingLeadsPage />
  }
  const isFilled = (value?: string | null) => value != null && value.trim() !== ''

  const isByFilters =
    isFilled(statePipeline) ||
    dateFrom != null ||
    dateTo != null ||
    valueUser != null ||
    isFilled(searchName) ||
    isFilled(searchPhone)

  return (
    <>
      <div className='d-flex flex-column w-100'>
        <div className='w-100 mb-2 d-flex justify-content-between flex-column flex-md-row gap-4'>
          <div className='d-flex align-items-center gap-4 col-md-10'>
            <div>
              <Form.Select
                aria-label='Default select example'
                value={pipelineId}
                onChange={(e) => {
                  const {value} = e.target
                  if (value) {
                    setPipelineId(value)
                  }
                }}
              >
                <option value=''>Select Pipeline</option>
                {dataPipelines.allPipelines.edges.map(({node}) => (
                  <option key={node.id} value={node.id}>
                    {node.name}
                  </option>
                ))}
              </Form.Select>
            </div>
            {flagView === 'card' ? (
              <div>
                <Form.Select
                  value={filterStage}
                  aria-label='Search stages'
                  onChange={(e) => setFilterStage(e.target.value as IFilterStage)}
                >
                  <option value='all'>View all</option>
                  <option value='soldDecline'>View Sold & Declined</option>
                  <option value='others'>View Active Stages</option>
                </Form.Select>
              </div>
            ) : (
              <div>
                <Form.Select
                  aria-label='Default select example'
                  onChange={(e) => {
                    const {value} = e.target
                    if (value) {
                      setStagePipelineId(value)
                      refreshList()
                    }
                    if (e.target.value === '') {
                      setStagePipelineId('')
                    }
                  }}
                >
                  <option value=''>View all stage</option>
                  {stagesBySelectedPipeline
                    ?.slice() // Create a copy before sorting (Apollo cache arrays are immutable)
                    .sort((a, b) => a.node.order - b.node.order)
                    .map(({node}) => (
                      <option key={node.id} value={node.id}>
                        {node.name}
                      </option>
                    ))}
                </Form.Select>
              </div>
            )}
            <div style={{minWidth: '130px'}}>
              <DateInput
                id='dateFrom'
                name='dateFrom'
                placeholder='Date From'
                value={formatDate(dateFrom)}
                onChange={(_, value) => {
                  if (!value) {
                    setDateFrom(null)
                    return
                  }
                  const date = new Date(`${value}T00:00:00`)
                  date.setHours(0, 0, 0, 0)
                  if (dateTo && date > dateTo) {
                    const newDateTo = new Date(date)
                    newDateTo.setHours(23, 59, 59, 999)
                    setDateTo(newDateTo)
                  }
                  setDateFrom(date)
                }}
              />
            </div>
            <div style={{minWidth: '130px'}}>
              <DateInput
                id='dateTo'
                name='dateTo'
                placeholder='Date To'
                value={formatDate(dateTo)}
                onChange={(_, value) => {
                  if (!value) {
                    setDateTo(null)
                    return
                  }
                  const date = new Date(`${value}T00:00:00`)
                  date.setHours(23, 59, 59, 999)
                  if (dateFrom && date < dateFrom) {
                    const newDateFrom = new Date(date)
                    newDateFrom.setHours(0, 0, 0, 0)
                    setDateFrom(newDateFrom)
                  }
                  setDateTo(date)
                }}
              />
            </div>
            <div>
              <Form.Control
                aria-label='Example text with button addon'
                aria-describedby='basic-addon1'
                name='search'
                type='search'
                value={searchNameText}
                onChange={(e) => {
                  setSearchNameText(e.target.value)
                  if (e.target.value === '') {
                    setSearchName('')
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    setSearchName(searchNameText)
                  }
                }}
                placeholder='Lead name'
              />
            </div>
            <div>
              <Form.Control
                aria-label='Example text with button addon'
                aria-describedby='basic-addon1'
                name='search'
                type='search'
                value={searchPhoneText}
                onChange={(e) => {
                  setSearchPhoneText(e.target.value)
                  if (e.target.value === '') {
                    setSearchPhone('')
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    setSearchPhone(searchPhoneText)
                  }
                }}
                placeholder='Lead phone number'
              />
            </div>
            {permissions && canExportLeads && (
              <div>
                <Form.Select
                  aria-label='Lead source'
                  value={valueUser}
                  name='leadSource'
                  onChange={(e) => {
                    const {value} = e.target
                    if (value) {
                      setValueUser(value)
                    }
                    if (e.target.value === '') {
                      setValueUser('')
                    }
                  }}
                >
                  <option value=''>Please select</option>
                  {dataMembers?.allUsersList.map((node) => (
                    <option key={node.id} value={node.id}>
                      {node.firstName + ' ' + node.lastName}
                    </option>
                  ))}
                </Form.Select>
              </div>
            )}
            <div>
              <Button
                variant={isMyLeadsActive ? 'primary' : 'outline-primary'}
                onClick={handleToggleMyLeads}
                disabled={!currentUserId}
              >
                <i className='bi bi-person-check me-1'></i>
                My Leads
              </Button>
            </div>

            <Button variant='secondary' onClick={() => research()} type='submit' id='button-addon1'>
              <i className='bi bi-search text-dark' />
            </Button>
          </div>
          {isLoadingChangeState && (
            <span>
              <Spinner animation='border' size='sm' />
            </span>
          )}
          <div className='d-flex align-items-center gap-2 flex-wrap'>
            {permissions && canExportLeads ? (
              <DropdownButton
                id='dropdown-basic-button'
                title='Actions'
                className='btn-actions-group'
              >
                {hasPermission(permissions, ALL_PERMISSIONS.ADD_LEAD) && (
                  <Dropdown.Item
                    className='px-4 mx-1 my-2 py-2 d-flex align-items-center gap-1'
                    onClick={onOpenAdd}
                  >
                    <i className='bi bi-person-vcard'></i>
                    Add Lead
                  </Dropdown.Item>
                )}
                <Dropdown.Item
                  onClick={() => {
                    const userNode = dataMembers?.allUsersList.find((node) => node.id === valueUser)

                    const stageSelectedNode = stageByPipeline?.find(
                      ({node}) => node.id === statePipeline
                    )?.node.name

                    const fullName = userNode ? `${userNode.firstName} ${userNode.lastName}` : ''
                    let assingType = 'by-user'
                    if (leadsSelected && leadsSelected.length > 0) {
                      assingType = 'by-leads-selected'
                    } else if (
                      (statePipeline != null && statePipeline !== '') ||
                      dateFrom != null ||
                      dateTo != null ||
                      (searchName != null && searchName !== '') ||
                      (searchPhone != null && searchPhone !== '')
                    )
                      assingType = 'by-filters'
                    const pipelineAux = dataPipelines?.allPipelines?.edges.find(
                      ({node}) => node.id === pipelineId
                    )
                    setIsExport(false)
                    setInfoReAssign({
                      fromUserId: valueUser,
                      fromUserName: fullName,
                      pipelineId: pipelineId,
                      pipelineName: pipelineAux?.node.name ?? '',
                      statePipeline: statePipeline,
                      stateNamePipeline: stageSelectedNode ?? '',
                      dateFrom: dateFrom,
                      dateTo: dateTo,
                      searchName: searchName,
                      searchPhone: searchPhone,
                      assignType: assingType,
                      leadsSelected: leadsSelected,
                    })
                    onOpenReAssign()
                  }}
                >
                  <i className='bi bi-person-up' />
                  Reassign Leads
                </Dropdown.Item>
                <Dropdown.Item
                  onClick={() => {
                    const pipelineAux = dataPipelines?.allPipelines?.edges.find(
                      ({node}) => node.id === pipelineId
                    )
                    setInfoAssignPipeline({
                      pipelineName: pipelineAux?.node.name ?? '',
                      pipelineId: pipelineId,
                    })
                    setTypeAssign('user')
                    onOpenAssignPipeline()
                  }}
                >
                  <i className='bi bi-person-check' />
                  Assign Revisor
                </Dropdown.Item>
                <Dropdown.Item
                  onClick={() => {
                    const pipelineAux = dataPipelines?.allPipelines?.edges.find(
                      ({node}) => node.id === pipelineId
                    )
                    setInfoAssignPipeline({
                      pipelineName: pipelineAux?.node.name ?? '',
                      pipelineId: pipelineId,
                    })
                    setTypeAssign('group')
                    onOpenAssignPipeline()
                  }}
                >
                  <i className='bi bi-person-fill-gear'></i>
                  Assign Group
                </Dropdown.Item>

                <Dropdown.Item
                  disabled={!isByFilters}
                  onClick={() => {
                    const userNode = dataMembers?.allUsersList.find((node) => node.id === valueUser)

                    const stageSelectedNode = stageByPipeline?.find(
                      ({node}) => node.id === statePipeline
                    )?.node.name

                    const fullName = userNode ? `${userNode.firstName} ${userNode.lastName}` : ''
                    const pipelineAux = dataPipelines?.allPipelines?.edges.find(
                      ({node}) => node.id === pipelineId
                    )
                    setIsExport(true)
                    setInfoReAssign({
                      fromUserId: valueUser,
                      fromUserName: fullName,
                      pipelineId: pipelineId,
                      pipelineName: pipelineAux?.node.name ?? '',
                      statePipeline: statePipeline,
                      stateNamePipeline: stageSelectedNode ?? '',
                      dateFrom: dateFrom,
                      dateTo: dateTo,
                      searchName: searchName,
                      searchPhone: searchPhone,
                      assignType: '',
                      leadsSelected: leadsSelected,
                    })
                    onOpenReAssign()
                  }}
                >
                  <i className='bi bi-download'></i>
                  Export Leads
                </Dropdown.Item>
              </DropdownButton>
            ) : (
              <Button
                variant='primary'
                onClick={onOpenAdd}
                disabled={!hasPermission(permissions, ALL_PERMISSIONS.ADD_LEAD)}
              >
                <i className='bi bi-person-vcard'></i>
                Add Lead
              </Button>
            )}
            {showStartDialQueueButton && (
              <Button
                variant='outline-success'
                onClick={handleStartDialQueue}
                disabled={mutatingDialQueue}
              >
                <i className='bi bi-telephone-forward me-1'></i>
                {mutatingDialQueue ? 'Starting...' : `Start Auto Dial Queue (${selectedLeadCount})`}
              </Button>
            )}
          </div>
        </div>
        <div className='w-100 mb-1 d-flex justify-content-between flex-column flex-md-row gap-4'>
          {permissions && canExportLeads ? (
            <div className='d-flex end-0 '>
              {' '}
              <Button
                variant='secondary'
                className='btn-sm btn-outline btn-export export-leads px-2'
                disabled={!isByFilters}
                onClick={() => {
                  const userNode = dataMembers?.allUsersList.find(
                    (node) => node.id === valueUser
                  )

                  const stageSelectedNode = stageByPipeline?.find(
                    ({node}) => node.id === statePipeline
                  )?.node.name

                  const fullName = userNode ? `${userNode.firstName} ${userNode.lastName}` : ''
                  const pipelineAux = dataPipelines?.allPipelines?.edges.find(
                    ({node}) => node.id === pipelineId
                  )
                  setIsExport(true)
                  setInfoReAssign({
                    fromUserId: valueUser,
                    fromUserName: fullName,
                    pipelineId: pipelineId,
                    pipelineName: pipelineAux?.node.name ?? '',
                    statePipeline: statePipeline,
                    stateNamePipeline: stageSelectedNode ?? '',
                    dateFrom: dateFrom,
                    dateTo: dateTo,
                    searchName: searchName,
                    searchPhone: searchPhone,
                    assignType: '',
                    leadsSelected: leadsSelected,
                  })
                  onOpenReAssign()
                }}
              >
                <i className='bi bi-download'></i>
                Export Leads
              </Button>
            </div>
          ) : (
            <div></div>
          )}
          <div className='d-flex justify-content-end gap-4 col-md-1'>
            <ButtonGroup className='btn-sm'>
              <Button
                variant='secondary'
                className='btn-sm'
                active={flagView === 'card'}
                onClick={() => setFlagView('card')}
              >
                <i className='bi bi-postcard' />
              </Button>
              <Button
                variant='secondary'
                className='btn-sm'
                active={flagView === 'list'}
                onClick={() => setFlagView('list')}
              >
                <i className='bi bi-card-list'></i>
              </Button>
            </ButtonGroup>
          </div>
        </div>

        {dialQueue && (
          <Alert
            variant={dialQueue.status === 'PAUSED' ? 'warning' : 'light'}
            className='mb-3 d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3'
          >
            <div>
              <div className='d-flex align-items-center gap-2 flex-wrap mb-1'>
                <strong>PolicyDial Auto Dial Queue</strong>
                <Badge bg={dialQueue.status === 'RUNNING' ? 'success' : dialQueue.status === 'PAUSED' ? 'warning' : 'secondary'}>
                  {dialQueue.status}
                </Badge>
                {dialQueue.campaign_id && <Badge bg='light' text='dark' className='border'>Campaign {dialQueue.campaign_id}</Badge>}
              </div>
              <div className='small text-muted'>
                {dialQueue.counts.completed} completed / {dialQueue.counts.total} selected
                {' • '}
                {dialQueue.counts.pending} pending
                {' • '}
                {dialQueue.counts.dialing} dialing
                {dialQueue.counts.failed > 0 ? ` • ${dialQueue.counts.failed} failed` : ''}
              </div>
              {dialQueue.current_item && (
                <div className='small mt-1'>
                  Current: <strong>{dialQueue.current_item.lead_name || dialQueue.current_item.phone}</strong>
                  {dialQueue.current_item.phone ? ` · ${dialQueue.current_item.phone}` : ''}
                </div>
              )}
              {dialQueue.next_dial_at && dialQueue.status === 'RUNNING' && !dialQueue.counts.dialing && (
                <div className='small mt-1 text-muted'>
                  Next call scheduled for {new Date(dialQueue.next_dial_at).toLocaleTimeString()}.
                </div>
              )}
              {dialQueue.last_error && (
                <div className='small text-danger mt-1'>{dialQueue.last_error}</div>
              )}
            </div>
            <div className='d-flex align-items-center gap-2 flex-wrap'>
              {loadingDialQueue && <Spinner animation='border' size='sm' />}
              {dialQueue.status === 'RUNNING' ? (
                <Button variant='outline-warning' onClick={handlePauseDialQueue} disabled={mutatingDialQueue}>
                  Pause
                </Button>
              ) : (
                <Button variant='outline-success' onClick={handleResumeDialQueue} disabled={mutatingDialQueue}>
                  Resume
                </Button>
              )}
              <Button variant='outline-danger' onClick={handleStopDialQueue} disabled={mutatingDialQueue}>
                Stop
              </Button>
            </div>
          </Alert>
        )}

        {flagView === 'card' ? (
          <div
            className='d-flex gap-2 container-draggs'
            style={{maxWidth: '100%', overflowX: 'auto'}}
          >
            <ContentDragDropLeads
              pipelineId={pipelineId}
              pipelines={dataPipelines.allPipelines.edges}
              onOpenEdit={onOpenEdit}
              setInfoLead={setInfoLead}
              updateListLeads={updateListLeads}
              flagLeads={flag}
              setLoadingState={(b: boolean) => setIsLoadingChangeState(b)}
              filterStage={filterStage}
              searchName={searchName}
              searchPhone={searchPhone}
              valueUser={valueUser}
              datesFrom={dateFrom}
              datesTo={dateTo}
              permissions={permissions}
              isRingIntegration={isRingIntegration}
              stagesDeclined={stagesDeclined}
            />
          </div>
        ) : (
          <div className='lead-list' style={{maxWidth: '100%', overflowX: 'auto'}}>
            <ContentListLeads
              pipelineId={pipelineId}
              onOpenEdit={onOpenEdit}
              setInfoLead={setInfoLead}
              flagLeads={flag}
              updateListLeads={updateListLeads}
              setLoadingState={(b: boolean) => setIsLoadingChangeState(b)}
              statePipeline={statePipeline}
              searchName={searchName}
              searchPhone={searchPhone}
              datesFrom={dateFrom}
              datesTo={dateTo}
              valueUser={valueUser}
              permissions={permissions}
              pageRefresh={pageRefresh}
              isRingIntegration={isRingIntegration}
              stagesDeclined={stagesDeclined}
              leadsSelected={leadsSelected}
              setLeadsSelected={setLeadsSelected}
              enableSelection={Boolean(
                canUsePolicyDialQueue && (isMyLeadsActive || canCreateSharedDialQueue)
              )}
            />
          </div>
        )}
      </div>

      {isOpenAdd && (
        <DrawerAddLead
          isOpen={isOpenAdd}
          onClose={onCloseAdd}
          updateListLeads={updateListLeads}
          initialPipelineId={pipelineId}
        />
      )}
      {isOpenEdit && infoLead && (
        <DrawerEditLead
          isOpen={isOpenEdit}
          onClose={handleCloseEditLead}
          info={infoLead}
          updateListLeads={updateListLeads}
        />
      )}
      {isOpenReAssign && infoReAssign && (
        <ModalReasingLeads
          isOpen={isOpenReAssign}
          onClose={onCloseReAssign}
          info={infoReAssign}
          updateListLeads={updateListLeads}
          iStagePipeline={stageByPipeline}
          isExport={isExport}
          flagView={flagView}
        />
      )}
      {isOpenAssignPipeline && infoAssignPipeline && (
        <ModalAssignAgentsToPipeline
          isOpen={isOpenAssignPipeline}
          onClose={onCloseAssignPipeline}
          info={infoAssignPipeline}
          typeAssign={typeAssign}
        />
      )}
    </>
  )
}
